import { fetchJSON } from "lib/fetch";
import siteConfig from "site-config";
import { stringify } from "querystring";
import { ICreateRollupSnapshotRequest, IRollupSnapshotResponse } from "../types/server-rollups";
import { flattenParams } from "helpers/query-helpers";
import { ItemKind, OrderByDirection, SpaceItemOrderByField } from "../types/base";
import { ROLLUP_SNAPSHOT_FOLDER } from "../utils/constants";
import { nullish } from "helpers/nullish";
import { IServerRollupResponse } from "helpers/server-rollup";
import { IReadSnapshotFilters } from "../hooks/utils";
import { buildSnapshotReviewQuery } from "helpers/query-dsl-builder";

export interface IRollupService {
    createSnapshot: (params: ICreateRollupSnapshotRequest) => Promise<IRollupSnapshotResponse>;
    getLastSnapshotId: (itemId: string, merchantId: string) => Promise<string>;
    getLastSnapshot: (itemId: string, merchantId: string) => Promise<nuorder.folder.v1.Item>;
    getSnapshot: (snapshotId: string, merchantId: string, withSnapshot?: boolean) => Promise<IRollupSnapshotResponse>;
    readSnapshotReview: (
        snapshotId: string,
        merchantId: string,
        filters: IReadSnapshotFilters,
    ) => Promise<IServerRollupResponse>;
    fetchSnapshotItems: (snapshotId: string, filters: any) => Promise<IServerRollupResponse>;
}

export const serverRollup: IRollupService = {
    createSnapshot: (params: ICreateRollupSnapshotRequest): Promise<IRollupSnapshotResponse> =>
        fetchJSON(`${siteConfig.goAPI}/v1/collections/rollup_snapshots`, {
            method: "POST",
            body: JSON.stringify({
                merchant_id: params.merchantId,
                parent_id: params.parentId,
                collection_ids: params.collectionIds,
                name: params.name,
            }),
        }).then(({ data } = {}) => data),
    getLastSnapshotId: async (itemId: string, merchantId: string): Promise<string> => {
        // folders with . are listed first
        const folders = await fetchJSON<nuorder.folder.v1.ListItemsResponse>(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/children?${stringify(
                flattenParams({
                    order_by: SpaceItemOrderByField.UpdateTime,
                    order_by_direction: OrderByDirection.Desc,
                }),
            )}`,
        );
        const rollupSnapshot = folders?.items?.shift();
        if (!rollupSnapshot) {
            return null;
        }
        return rollupSnapshot.id;
    },
    getLastSnapshot: async (itemId: string, merchantId: string): Promise<nuorder.folder.v1.Item> => {
        // folders with . are listed first
        const folders = await fetchJSON<nuorder.folder.v1.ListItemsResponse>(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/children?${stringify(
                flattenParams({
                    filter: {
                        item_kind: ItemKind.Folder,
                        not: false,
                    },
                    order_by: SpaceItemOrderByField.Name,
                    order_by_direction: OrderByDirection.Asc,
                }),
            )}`,
        );
        const rollupSnapshotFolder = folders?.items?.find((folder) => folder.name === ROLLUP_SNAPSHOT_FOLDER);
        if (!rollupSnapshotFolder) {
            return null;
        }

        // grab last snapshot
        const snapshot = await fetchJSON<nuorder.folder.v1.ListItemsResponse>(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${rollupSnapshotFolder.id}/children?${stringify(
                flattenParams({
                    filter: {
                        item_kind: ItemKind.CollectionRollupSnapshot,
                        not: false,
                    },
                    order_by: SpaceItemOrderByField.CreateTime,
                    order_by_direction: OrderByDirection.Desc,
                }),
            )}`,
        );

        return snapshot?.items?.[0];
    },
    getSnapshot: (snapshotId: string, merchantId: string, withSnapshot?: boolean): Promise<IRollupSnapshotResponse> =>
        fetchJSON(
            `${siteConfig.goAPI}/v1/collections/rollup_snapshots/${snapshotId}?${stringify({
                merchant_id: merchantId,
                ignore_item_display_preferences: true,
                without_allocation_dimensions: true,
                with_collection_status: true,
                ...(!nullish(withSnapshot) && { with_snapshot: withSnapshot }),
            })}`,
        ).then(({ data } = {}) => data),
    readSnapshotReview: (
        snapshotId: string,
        merchantId: string,
        {
            doorDimensionId,
            deliveryDimensionId,
            sizeDimensionId,
            doorId,
            productId,
            collectionId,
        }: IReadSnapshotFilters,
    ): Promise<IServerRollupResponse> =>
        fetchJSON(`${siteConfig.goAPI}/v1/collections/rollup_snapshots/${snapshotId}/read`, {
            method: "POST",
            body: JSON.stringify(
                buildSnapshotReviewQuery({
                    merchantId,
                    doorDimensionId,
                    dateDimensionId: deliveryDimensionId,
                    sizeDimensionId,
                    productId,
                    doorId,
                    collectionId,
                }),
            ),
        }),
    fetchSnapshotItems: (snapshotId: string, filters: any): Promise<IServerRollupResponse> =>
        fetchJSON(`${siteConfig.goAPI}/v1/collections/rollup_snapshots/${snapshotId}/read`, {
            method: "POST",
            body: JSON.stringify(filters),
        }),
};
